import React, { useEffect, useMemo, useRef, useState } from "react";
import Loading from "../../Common/Loading";
import popup_bg1 from "../../Common/Images/ImagesForAlert/popup_bg1.png";
import alert_close_n from "../../Common/Images/ImagesForAlert/alert_close_n.png";
import alert_close_o from "../../Common/Images/ImagesForAlert/alert_close_o.png";

const API_BASE = `${(process.env.REACT_APP_DOCUMENT_LINK ||
  "https://staging.sysadmin.elearnoncloud.com/").replace(/\/+$/, "")}/`;

interface Props {
  mode: "A" | "R";
  avatarTitle?: string;
  purchasedByNos?: number;
  onClose: () => void;
  onSaveSuccess: () => void;
  showWarning: (msg: string) => void;
  showConfirm: (msg: string, onConfirm: () => void) => void;
}

interface Dealer {
  Name: string;
  SchoolCode: string;
  Country: string;
}

interface AvatarImage {
  name: string;
  url: string;
}

interface SelectedDealer {
  Country: string;
  SchoolCode: string;
}

type ImageKey = "avatar" | "a" | "b" | "c" | "d";

const EL_AvatarARD: React.FC<Props> = ({
  mode,
  avatarTitle,
  purchasedByNos = 0,
  onClose,
  onSaveSuccess,
  showWarning,
  showConfirm,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const dropAreaRef = useRef<HTMLDivElement | null>(null);
  const dealerRequestRef = useRef(0);
  const loadingCountRef = useRef(0);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  /*
   * One loading indicator for every async operation. A counter is used
   * because operations are nested:
   * loadInitialData -> loadExistingAvatar -> fetchAvatarImages
   */
  const startLoading = () => {
    loadingCountRef.current += 1;
    setLoading(true);
  };

  const stopLoading = () => {
    loadingCountRef.current = Math.max(0, loadingCountRef.current - 1);

    if (loadingCountRef.current === 0) {
      setLoading(false);
    }
  };

  /*
   * All warnings continue through the existing EL_ManageAvatars custom
   * alert. No native browser alert is used.
   */
  const showCustomDuplicateAlert = (message: string) => {
    showWarning(message);
  };

  /*
   * VB:
   *   Add    -> rdoPicture.Checked = True, hidType = "Picture"
   *   Modify -> type loaded from DB
   */
  const [type, setType] = useState(mode === "A" ? "Picture" : "");

  /*
   * VB ddlAvatarTitle is a LABEL.
   * ADD: populated automatically from *_avatar.png.
   * MODIFY: the existing title (never changes).
   */
  const [selectedTitle, setSelectedTitle] = useState(
    mode === "R" ? avatarTitle || "" : "",
  );

  const [price, setPrice] = useState("");

  const [startDate, setStartDate] = useState(
    () => new Date().toISOString().split("T")[0],
  );

  const [countries, setCountries] = useState<string[]>([]);
  const [dealers, setDealers] = useState<Dealer[]>([]);

  const [chkSchools, setChkSchools] = useState(false);
  const [chkHomes, setChkHomes] = useState(false);

  const [selectedSchoolCountries, setSelectedSchoolCountries] = useState<
    string[]
  >([]);

  const [selectedHomeCountries, setSelectedHomeCountries] = useState<string[]>(
    [],
  );

  const [selectedDealers, setSelectedDealers] = useState<SelectedDealer[]>([]);

  /*
   * Files chosen in the upload control.
   *
   * ADD    : exactly 5 files (required).
   * MODIFY : 1 to 5 files (optional) - only these blobs are overwritten.
   */
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  /*
   * Local previews of the selected files.
   * In MODIFY mode they override the existing Azure images.
   */
  const [previewUrls, setPreviewUrls] = useState<
    Partial<Record<ImageKey, string>>
  >({});

  /* Existing images in MODIFY mode. */
  const [avatarImages, setAvatarImages] = useState<AvatarImage[]>([]);

  /*
   * -----------------------------------------------------------------------
   * INITIAL LOAD (VB Page_Load)
   * -----------------------------------------------------------------------
   */
  useEffect(() => {
    loadInitialData();
    // Initial load only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Revoke old blob: URLs whenever the preview set changes / on unmount. */
  useEffect(() => {
    return () => {
      Object.values(previewUrls).forEach((url) => {
        if (url && url.startsWith("blob:")) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [previewUrls]);

  /* Whenever selected home countries change, VB recreates the dealer table. */
  useEffect(() => {
    if (selectedHomeCountries.length === 0) {
      setDealers([]);
      setSelectedDealers([]);
      return;
    }

    fetchDealers(selectedHomeCountries);
  }, [selectedHomeCountries]);

  const loadInitialData = async () => {
    startLoading();

    try {
      /*
       * VB LoadCountry():
       *   SELECT Major FROM EL_Attributes WHERE Attribute='COUNTRY'
       *   ORDER BY Major
       * Served by the avatar-specific Django action.
       */
      const countryResponse = await fetch(
        `${API_BASE}api/manage-avatars/countries/`,
      );

      if (countryResponse.ok) {
        const countryData = await countryResponse.json();

        if (Array.isArray(countryData)) {
          setCountries(
            countryData
              .map((country) => String(country).trim())
              .filter(Boolean),
          );
        }
      }

      if (mode === "R" && avatarTitle) {
        await loadExistingAvatar(avatarTitle);
      }

      if (mode === "A") {
        setType("Picture");
        setSelectedTitle("");
        setPrice("");
        setSelectedFiles([]);
        setPreviewUrls({});
      }
    } catch (error) {
      console.error("EL_AvatarARD initial load error:", error);
    } finally {
      stopLoading();
    }
  };

  /*
   * -----------------------------------------------------------------------
   * LOAD EXISTING AVATAR (VB PopulateAvatars)
   * -----------------------------------------------------------------------
   */
  const loadExistingAvatar = async (title: string) => {
    startLoading();

    try {
      setSelectedTitle(title);

      const response = await fetch(
        `${API_BASE}api/manage-avatars/${encodeURIComponent(title)}/`,
      );

      if (!response.ok) {
        showWarning("Unable to load avatar details.");
        return;
      }

      const data = await response.json();

      const avatar = data?.avatar || {};
      const allocations = data?.allocations || {};

      setType(String(avatar.Type || "").trim());

      if (
        avatar.Price !== null &&
        avatar.Price !== undefined &&
        avatar.Price !== ""
      ) {
        setPrice(String(avatar.Price));
      } else {
        setPrice("");
      }

      if (avatar.StartDate) {
        const rawStartDate = String(avatar.StartDate);
        const dateOnly = rawStartDate.split("T")[0].split(" ")[0];

        if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) {
          setStartDate(dateOnly);
        } else {
          const date = new Date(rawStartDate);

          if (!Number.isNaN(date.getTime())) {
            setStartDate(
              `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
                2,
                "0",
              )}-${String(date.getDate()).padStart(2, "0")}`,
            );
          }
        }
      }

      /* VB: A = Schools + Homes, S = Schools, F = Homes */
      const allocatedToDb = String(avatar.AllocatedTo || "").toUpperCase();

      setChkSchools(allocatedToDb === "A" || allocatedToDb === "S");
      setChkHomes(allocatedToDb === "A" || allocatedToDb === "F");

      const schoolCountries = Array.isArray(allocations.SchoolCountries)
        ? allocations.SchoolCountries
        : [];

      setSelectedSchoolCountries(
        schoolCountries.map((x: unknown) => String(x).trim()).filter(Boolean),
      );

      const homeDealers = Array.isArray(allocations.HomeDealers)
        ? allocations.HomeDealers
        : [];

      const normalizedDealers: SelectedDealer[] = homeDealers
        .map((dealer: any) => ({
          Country: String(dealer?.Country || "").trim(),
          SchoolCode: String(dealer?.SchoolCode || "").trim(),
        }))
        .filter(
          (dealer: SelectedDealer) => dealer.Country && dealer.SchoolCode,
        );

      setSelectedDealers(normalizedDealers);

      /* VB derives the selected home countries from the dealer rows. */
      const homeCountries = Array.from(
        new Set(normalizedDealers.map((dealer) => dealer.Country)),
      );

      setSelectedHomeCountries(homeCountries);

      await fetchAvatarImages(title);
    } catch (error) {
      console.error("Error loading existing avatar:", error);
      showWarning("Unable to load avatar details.");
    } finally {
      stopLoading();
    }
  };

  /*
   * -----------------------------------------------------------------------
   * EXISTING IMAGE URLS
   * -----------------------------------------------------------------------
   * VB:
   *   /Storage/assets/AvatarNew/<title>_avatar.png
   *   /Storage/assets/AvatarNew/Result_Avatar/<title>_a|b|c|d.png
   */
  const fetchAvatarImages = async (title: string) => {
    startLoading();

    try {
      const response = await fetch(
        `${API_BASE}api/manage_avatars/get_image_urls/?image_name=${encodeURIComponent(
          title,
        )}`,
      );

      if (!response.ok) {
        setAvatarImages([]);
        return;
      }

      const data = await response.json();

      setAvatarImages(Array.isArray(data?.urls) ? data.urls : []);
    } catch (error) {
      console.error("Error fetching avatar images:", error);
      setAvatarImages([]);
    } finally {
      stopLoading();
    }
  };

  /*
   * -----------------------------------------------------------------------
   * DEALERS (VB CreateTableCountyE_Dealer -> LoadeDealer)
   * -----------------------------------------------------------------------
   */
  const fetchDealers = async (countryList: string[]) => {
    const requestId = ++dealerRequestRef.current;
    startLoading();

    try {
      const requests = countryList.map(async (country) => {
        try {
          const response = await fetch(
            `${API_BASE}api/manage-avatars/dealers/?country=${encodeURIComponent(
              country,
            )}`,
          );

          if (!response.ok) {
            return [];
          }

          const data = await response.json();

          if (!Array.isArray(data)) {
            return [];
          }

          return data.map((dealer: any) => ({
            Name: String(dealer?.Name || ""),
            SchoolCode: String(dealer?.SchoolCode || ""),
            Country: country,
          }));
        } catch (error) {
          console.error(`Error loading dealers for country ${country}:`, error);
          return [];
        }
      });

      const results = await Promise.all(requests);

      /* Ignore a slower response from an older country selection. */
      if (requestId !== dealerRequestRef.current) {
        return;
      }

      const merged = results.flat();

      const unique = merged.filter(
        (dealer, index, array) =>
          index ===
          array.findIndex(
            (x) =>
              x.Country === dealer.Country && x.SchoolCode === dealer.SchoolCode,
          ),
      );

      setDealers(unique);

      /* Drop dealer selections of deselected countries. */
      setSelectedDealers((current) =>
        current.filter((dealer) =>
          countryList.some(
            (country) => country.toUpperCase() === dealer.Country.toUpperCase(),
          ),
        ),
      );
    } catch (error) {
      if (requestId !== dealerRequestRef.current) {
        return;
      }

      console.error("Dealer loading error:", error);
      setDealers([]);
    } finally {
      stopLoading();
    }
  };

  /*
   * -----------------------------------------------------------------------
   * TYPE
   * -----------------------------------------------------------------------
   */
  const handleTypeChange = (value: string) => {
    setType(value);
  };

  /*
   * -----------------------------------------------------------------------
   * ALLOCATION (VB fn_SetAllocatedTo)
   * -----------------------------------------------------------------------
   */
  const allocatedTo = useMemo(() => {
    if (chkSchools && chkHomes) {
      return "A";
    }

    if (chkSchools) {
      return "S";
    }

    if (chkHomes) {
      return "F";
    }

    return "";
  }, [chkSchools, chkHomes]);

  /*
   * Selections are kept when a section is unchecked so re-checking it
   * restores them, like the legacy UI.
   */
  const handleSchoolsToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChkSchools(event.target.checked);
  };

  const handleHomesToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChkHomes(event.target.checked);
  };

  const toggleSchoolCountry = (country: string) => {
    setSelectedSchoolCountries((current) =>
      current.includes(country)
        ? current.filter((item) => item !== country)
        : [...current, country],
    );
  };

  const toggleHomeCountry = (country: string) => {
    setSelectedHomeCountries((current) =>
      current.includes(country)
        ? current.filter((item) => item !== country)
        : [...current, country],
    );
  };

  const toggleDealer = (dealer: Dealer) => {
    setSelectedDealers((current) => {
      const exists = current.some(
        (item) =>
          item.Country === dealer.Country &&
          item.SchoolCode === dealer.SchoolCode,
      );

      if (exists) {
        return current.filter(
          (item) =>
            !(
              item.Country === dealer.Country &&
              item.SchoolCode === dealer.SchoolCode
            ),
        );
      }

      return [
        ...current,
        { Country: dealer.Country, SchoolCode: dealer.SchoolCode },
      ];
    });
  };

  /*
   * -----------------------------------------------------------------------
   * IMAGE HELPERS
   * -----------------------------------------------------------------------
   */
  const getImageKeyFromFileName = (
    fileName: string,
    title: string,
  ): ImageKey | null => {
    const name = fileName.toLowerCase();
    const lowerTitle = title.toLowerCase();

    if (name === `${lowerTitle}_avatar.png`) return "avatar";
    if (name === `${lowerTitle}_a.png`) return "a";
    if (name === `${lowerTitle}_b.png`) return "b";
    if (name === `${lowerTitle}_c.png`) return "c";
    if (name === `${lowerTitle}_d.png`) return "d";

    return null;
  };

  /* Build previews for the selected files (old blob URLs are revoked by
     the previewUrls effect cleanup). */
  const applySelection = (files: File[], lowerTitle: string) => {
    const newPreviewUrls: Partial<Record<ImageKey, string>> = {};

    for (const file of files) {
      const key = getImageKeyFromFileName(file.name, lowerTitle);

      if (key) {
        newPreviewUrls[key] = URL.createObjectURL(file);
      }
    }

    setSelectedFiles(files);
    setPreviewUrls(newPreviewUrls);
  };

  /*
   * VB clearAvatarImages() - ADD mode: clears input, title and all images.
   */
  const clearAvatarImages = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    setSelectedFiles([]);
    setSelectedTitle("");
    setPreviewUrls({});
  };

  /*
   * VB validateRevisedAvatarImages() error path - MODIFY mode: only the
   * native file input is cleared; the title and the previous valid
   * selection / previews stay.
   */
  const rejectSelection = (message: string) => {
    showWarning(message);

    if (mode === "A") {
      clearAvatarImages();
    } else if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    return false;
  };

  /*
   * -----------------------------------------------------------------------
   * VB validateAvatarImages() - ADD mode
   * -----------------------------------------------------------------------
   * 1. Exactly 5 files.
   * 2. One *_avatar.png (case-insensitive) gives the title.
   * 3. Names compared in lowercase: title_avatar/a/b/c/d.png.
   * 4. PNG only, no duplicates, all five present.
   */
  const validateNewAvatarImages = (files: File[]) => {
    if (files.length !== 5) {
      return rejectSelection(
        "Please select exactly 5 images:<br><br>" +
          "title_avatar.png<br>" +
          "title_a.png<br>" +
          "title_b.png<br>" +
          "title_c.png<br>" +
          "title_d.png",
      );
    }

    let title = "";
    let avatarFileFound = false;

    for (const file of files) {
      const fileName = file.name;

      if (fileName.toLowerCase().endsWith("_avatar.png")) {
        title = fileName.substring(0, fileName.length - "_avatar.png".length);
        avatarFileFound = true;
        break;
      }
    }

    if (!avatarFileFound || title === "") {
      return rejectSelection(
        "Missing main image.<br><br>" +
          "One image must be named:<br>" +
          "title_avatar.png",
      );
    }

    const lowerTitle = title.toLowerCase();

    const validNames = [
      `${lowerTitle}_avatar.png`,
      `${lowerTitle}_a.png`,
      `${lowerTitle}_b.png`,
      `${lowerTitle}_c.png`,
      `${lowerTitle}_d.png`,
    ];

    const selectedNames: string[] = [];

    for (const file of files) {
      const fileName = file.name.toLowerCase();

      if (!fileName.endsWith(".png")) {
        return rejectSelection(
          "Invalid file type:<br><br>" +
            fileName +
            "<br><br>Only PNG images are allowed.",
        );
      }

      if (!validNames.includes(fileName)) {
        return rejectSelection(
          "Invalid file name:<br><br>" +
            fileName +
            "<br><br>Required files for title '" +
            lowerTitle +
            "':<br><br>" +
            validNames.join("<br>"),
        );
      }

      if (selectedNames.includes(fileName)) {
        showCustomDuplicateAlert(
          "Duplicate file selected:<br><br>" +
            fileName +
            "<br><br>Please select all 5 different images.",
        );
        clearAvatarImages();
        return false;
      }

      selectedNames.push(fileName);
    }

    for (const requiredName of validNames) {
      if (!selectedNames.includes(requiredName)) {
        return rejectSelection(
          "Missing image:<br><br>" +
            requiredName +
            "<br><br>Please select all 5 required images.",
        );
      }
    }

    /* titleControl.innerText = lowerTitle (DB capitalisation by backend). */
    setSelectedTitle(lowerTitle);
    applySelection(files, lowerTitle);

    return true;
  };

  /*
   * -----------------------------------------------------------------------
   * VB validateRevisedAvatarImages() - MODIFY mode
   * -----------------------------------------------------------------------
   * 1 to 5 images, each must be one of the five names of the EXISTING
   * title (case-insensitive), no duplicates. Only selected slots are
   * replaced; the title never changes.
   */
  const validateRevisedAvatarImages = (files: File[]) => {
    const title = selectedTitle.trim().toLowerCase();

    if (!title) {
      return rejectSelection("Existing avatar title is missing.");
    }

    const validNames = [
      `${title}_avatar.png`,
      `${title}_a.png`,
      `${title}_b.png`,
      `${title}_c.png`,
      `${title}_d.png`,
    ];

    if (files.length < 1 || files.length > 5) {
      return rejectSelection("Upload between 1 and 5 images.");
    }

    const selectedNames: string[] = [];

    for (const file of files) {
      const fileName = file.name.toLowerCase();

      if (!fileName.endsWith(".png") || !validNames.includes(fileName)) {
        return rejectSelection(
          "Invalid image name: " +
            file.name +
            "<br><br>Expected one of:<br>" +
            validNames.join("<br>"),
        );
      }

      if (selectedNames.includes(fileName)) {
        return rejectSelection("Duplicate image selected: " + file.name);
      }

      selectedNames.push(fileName);
    }

    applySelection(files, title);

    return true;
  };

  /* VB validateAvatarImages(input) entry point. */
  const validateAvatarImages = (files: File[]) => {
    if (files.length === 0) {
      return false;
    }

    if (mode === "R") {
      return validateRevisedAvatarImages(files);
    }

    return validateNewAvatarImages(files);
  };

  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    validateAvatarImages(Array.from(event.target.files || []));
  };

  /*
   * -----------------------------------------------------------------------
   * DRAG & DROP
   * -----------------------------------------------------------------------
   */
  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (!dropAreaRef.current) return;

    dropAreaRef.current.style.backgroundColor = "#E8F0FF";
    dropAreaRef.current.style.borderColor = "#4A6CF7";
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (!dropAreaRef.current) return;

    dropAreaRef.current.style.backgroundColor = "#F8FAFF";
    dropAreaRef.current.style.borderColor = "#82A0F6";
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (dropAreaRef.current) {
      dropAreaRef.current.style.backgroundColor = "#F8FAFF";
      dropAreaRef.current.style.borderColor = "#82A0F6";
    }

    validateAvatarImages(Array.from(event.dataTransfer.files || []));
  };

  /*
   * -----------------------------------------------------------------------
   * SAVE ENABLE / DISABLE (VB fn_EnableOrDisabledSave)
   * -----------------------------------------------------------------------
   * Disabled when:
   *   allocation == "" OR (ADD && title == "") OR price == ""
   *   OR type == "" OR (ADD && no files)
   * MODIFY never requires files.
   */
  const saveEnabled = useMemo(() => {
    if (allocatedTo === "") return false;
    if (mode === "A" && selectedTitle.trim() === "") return false;
    if (price.trim() === "") return false;
    if (type.trim() === "") return false;
    if (mode === "A" && selectedFiles.length === 0) return false;

    return true;
  }, [allocatedTo, mode, price, selectedFiles.length, selectedTitle, type]);

  /*
   * -----------------------------------------------------------------------
   * VALIDATION (VB fn_Validate)
   * -----------------------------------------------------------------------
   * VB only collects school countries when "schools" is checked and
   * dealers when "homes" is checked.
   */
  const validateBeforeSave = (): boolean => {
    const effectiveSchools = chkSchools ? selectedSchoolCountries : [];
    const effectiveDealers = chkHomes ? selectedDealers : [];

    if (!type) {
      showWarning("Plesase select a type.");
      return false;
    }

    if (effectiveSchools.length === 0 && effectiveDealers.length === 0) {
      showWarning("Plesase select Allocated to.");
      return false;
    }

    if (chkSchools && effectiveSchools.length === 0) {
      showWarning("Plesase select Allocated to Schools.");
      return false;
    }

    if (chkHomes && effectiveDealers.length === 0) {
      showWarning("Plesase select Allocated to home users.");
      return false;
    }

    /* Final guard for ADD. */
    if (mode === "A") {
      if (!selectedTitle.trim()) {
        showWarning(
          "Missing main image.<br><br>One image must be named:<br>title_avatar.png",
        );
        return false;
      }

      if (selectedFiles.length !== 5) {
        showWarning(
          "Please select exactly 5 images:<br><br>" +
            "title_avatar.png<br>" +
            "title_a.png<br>" +
            "title_b.png<br>" +
            "title_c.png<br>" +
            "title_d.png",
        );
        return false;
      }
    }

    return true;
  };

  /*
   * -----------------------------------------------------------------------
   * BUILD FORM DATA
   * -----------------------------------------------------------------------
   * VB fn_ARDAvatarTOBlob uploads whenever the upload control has files,
   * in BOTH Add and Modify mode.
   */
  const buildFormData = () => {
    const formData = new FormData();

    formData.append("Title", selectedTitle.trim());
    formData.append("Type", type.trim());
    formData.append("Price", price.trim());
    formData.append("StartDate", startDate);
    formData.append(
      "AllocatedSchools",
      JSON.stringify(chkSchools ? selectedSchoolCountries : []),
    );
    formData.append(
      "AllocatedHomes",
      JSON.stringify(chkHomes ? selectedDealers : []),
    );
    formData.append("AllocatedTo", allocatedTo);

    selectedFiles.forEach((file) => {
      formData.append("AvatarImages", file, file.name);
    });

    return formData;
  };

  /*
   * -----------------------------------------------------------------------
   * SAVE
   * -----------------------------------------------------------------------
   */
  const handleSave = () => {
    if (!validateBeforeSave()) {
      return;
    }

    if (mode === "A") {
      showConfirm("Do you want add the avatar ?", () => {
        performSave();
      });

      return;
    }

    if (purchasedByNos > 0) {
      showConfirm(
        `This avatar has been purchased by (“${purchasedByNos}”) users. Do you want to save changes?`,
        () => {
          performSave();
        },
      );

      return;
    }

    performSave();
  };

  const performSave = async () => {
    if (saving) {
      return;
    }

    setSaving(true);
    startLoading();

    try {
      const url =
        mode === "A"
          ? `${API_BASE}api/manage-avatars/`
          : `${API_BASE}api/manage-avatars/${encodeURIComponent(
              avatarTitle || selectedTitle,
            )}/`;

      const method = mode === "A" ? "POST" : "PUT";

      /* Do NOT set Content-Type; the browser adds the multipart boundary. */
      const response = await fetch(url, {
        method,
        body: buildFormData(),
      });

      if (!response.ok) {
        let errorMessage = "Error saving avatar.";

        try {
          const errorData = await response.json();

          if (errorData?.error) {
            errorMessage = errorData.error;
          } else if (errorData?.detail) {
            errorMessage = errorData.detail;
          }
        } catch {
          // Keep default error message.
        }

        if (errorMessage.toLowerCase().includes("already exist")) {
          showCustomDuplicateAlert(errorMessage);
        } else {
          showWarning(errorMessage);
        }
        return;
      }

      const result = await response.json().catch(() => ({}));

      showWarning(
        String(
          result?.message ||
            (mode === "A" ? "Added successfully." : "Updated successfully."),
        ),
      );
      onSaveSuccess();
      onClose();
    } catch (error) {
      console.error("Avatar save error:", error);
      showWarning("Network Error");
    } finally {
      setSaving(false);
      stopLoading();
    }
  };

  /*
   * -----------------------------------------------------------------------
   * EXISTING IMAGE RESOLUTION (MODIFY)
   * -----------------------------------------------------------------------
   * Matched strictly by file name (no positional fallback, which could
   * shift images into the wrong slot when one blob is missing).
   */
  const existingImageByKey = useMemo(() => {
    const result: Partial<Record<ImageKey, AvatarImage>> = {};

    const title = selectedTitle.toLowerCase();

    for (const image of avatarImages) {
      const fileName = image.name.toLowerCase();

      if (fileName === `${title}_avatar.png`) {
        result.avatar = image;
      } else if (fileName === `${title}_a.png`) {
        result.a = image;
      } else if (fileName === `${title}_b.png`) {
        result.b = image;
      } else if (fileName === `${title}_c.png`) {
        result.c = image;
      } else if (fileName === `${title}_d.png`) {
        result.d = image;
      }
    }

    return result;
  }, [avatarImages, selectedTitle]);

  /* Newly selected file preview wins; MODIFY falls back to Azure image. */
  const getImageSource = (key: ImageKey): string | undefined => {
    if (previewUrls[key]) {
      return previewUrls[key];
    }

    if (mode === "R") {
      return existingImageByKey[key]?.url;
    }

    return undefined;
  };

  /*
   * -----------------------------------------------------------------------
   * STYLES
   * -----------------------------------------------------------------------
   */
  const styles = {
    overlay: {
      position: "fixed" as const,
      top: 0,
      left: 0,
      width: "100vw",
      height: "100vh",
      backgroundColor: "rgba(0,0,0,0.5)",
      zIndex: 9999,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      margin: 0,
      padding: 0,
    },

    modal: {
      backgroundColor: "#E8E8FF",
      width: "100%",
      height: "100vh",
      display: "flex",
      flexDirection: "column" as const,
      border: "none",
      fontFamily: "Arial",
      fontSize: "11pt",
      padding: 0,
      boxSizing: "border-box" as const,
      overflow: "hidden",
    },

    popupHeader: {
      backgroundImage: `url(${popup_bg1})`,
      backgroundRepeat: "repeat-x",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      borderBottom: "1px solid #000",
      height: "30px",
      minHeight: "30px",
      color: "white",
      fontFamily: "Calibri, Arial, sans-serif",
      fontSize: "11pt",
      fontWeight: "bold" as const,
      padding: "0 10px",
    },

    maroonHeader: {
      color: "White",
      backgroundColor: "Maroon",
      padding: "5px",
      fontSize: "12pt",
      fontFamily: "Arial",
      textAlign: "center" as const,
      fontWeight: "bold" as const,
      margin: "10px 10px 0 10px",
    },

    body: {
      flex: 1,
      padding: "10px",
      overflow: "auto",
    },

    mainTable: {
      width: "100%",
      borderCollapse: "collapse" as const,
      borderSpacing: 0,
    },

    leftColumn: {
      width: "40%",
      borderRight: "1px solid #82A0F6",
      borderBottom: "1px solid #82A0F6",
      paddingTop: "5px",
      verticalAlign: "top" as const,
    },

    leftInnerTable: {
      width: "98%",
      borderCollapse: "collapse" as const,
      borderSpacing: 0,
    },

    labelCell: {
      width: "20%",
      textAlign: "left" as const,
      padding: "5px",
      verticalAlign: "middle" as const,
      whiteSpace: "nowrap" as const,
    },

    valueCell: {
      width: "80%",
      textAlign: "left" as const,
      padding: "5px",
      verticalAlign: "middle" as const,
    },

    cssTextBox: {
      padding: "2px",
      border: "1px solid #82A0F6",
      fontFamily: "Arial",
      fontSize: "11pt",
      backgroundColor: "#FFFFFF",
      boxSizing: "border-box" as const,
    },

    titleBox: {
      display: "inline-block",
      width: "110px",
      minHeight: "15px",
      backgroundColor: "#FFFFFF",
      border: "1px solid #82A0F6",
      color: "#000000",
      padding: "3px",
      verticalAlign: "middle" as const,
      boxSizing: "content-box" as const,
      overflow: "hidden",
      whiteSpace: "nowrap" as const,
      textOverflow: "ellipsis",
    },

    uploadDrop: {
      display: "inline-block",
      width: "300px",
      padding: "12px",
      marginLeft: "5px",
      verticalAlign: "middle" as const,
      border: "2px dashed #82A0F6",
      textAlign: "center" as const,
      cursor: "pointer",
      backgroundColor: "#F8FAFF",
      boxSizing: "border-box" as const,
    },

    imageOuter: {
      width: "100%",
      border: "1px solid #82A0F6",
      backgroundColor: "#FFFBFF",
      boxSizing: "border-box" as const,
      borderCollapse: "collapse" as const,
    },

    mainImageCell: {
      width: "70%",
      height: "250px",
      borderRight: "1px solid #82A0F6",
      textAlign: "center" as const,
      verticalAlign: "middle" as const,
      padding: "5px",
    },

    smallImageCell: {
      width: "15%",
      height: "125px",
      borderRight: "1px solid #82A0F6",
      borderBottom: "1px solid #82A0F6",
      textAlign: "center" as const,
      verticalAlign: "middle" as const,
      padding: "5px",
    },

    smallImageCellLast: {
      width: "15%",
      height: "125px",
      borderBottom: "1px solid #82A0F6",
      textAlign: "center" as const,
      verticalAlign: "middle" as const,
      padding: "5px",
    },

    mainImage: {
      height: "250px",
      width: "100%",
      objectFit: "contain" as const,
      border: "none",
      display: "block",
    },

    resultImage: {
      height: "110px",
      width: "110px",
      maxWidth: "100%",
      objectFit: "contain" as const,
      border: "none",
    },

    rightSpacer: {
      width: "1%",
      borderLeft: "1px solid #82A0F6",
      borderBottom: "1px solid #82A0F6",
      verticalAlign: "top" as const,
    },

    rightColumn: {
      width: "59%",
      borderBottom: "1px solid #82A0F6",
      paddingTop: "5px",
      verticalAlign: "top" as const,
    },

    allocationTable: {
      width: "99%",
      borderCollapse: "collapse" as const,
      borderSpacing: 0,
    },

    allocationHeader: {
      backgroundColor: "#ECFACB",
      padding: "5px 3px",
      textAlign: "left" as const,
      fontWeight: "bold" as const,
    },

    allocationGrid: {
      height: "200px",
      overflowX: "hidden" as const,
      overflowY: "auto" as const,
      border: "1px solid #82A0F6",
      backgroundColor: "#FFFFFF",
    },

    countryTable: {
      width: "100%",
      borderCollapse: "collapse" as const,
      borderSpacing: 0,
    },

    countryHeader: {
      backgroundColor: "#c7ddfd",
      height: "25px",
    },

    countryHeaderCell: {
      padding: "5px",
      borderBottom: "1px solid #82A0F6",
      borderRight: "1px solid #82A0F6",
      textAlign: "left" as const,
      whiteSpace: "nowrap" as const,
    },

    countryCell: {
      padding: "5px",
      borderRight: "1px solid #82A0F6",
      verticalAlign: "middle" as const,
    },

    homeCountryWrapper: {
      width: "100%",
      display: "flex",
      alignItems: "stretch",
      overflow: "hidden",
    },

    homeCountryColumn: {
      width: "28%",
      minWidth: "0",
      padding: "0",
    },

    dealerColumn: {
      width: "70%",
      marginLeft: "2%",
      minWidth: "0",
    },

    homeGrid: {
      height: "200px",
      width: "100%",
      overflowX: "hidden" as const,
      overflowY: "auto" as const,
      border: "1px solid #82A0F6",
      backgroundColor: "#FFFFFF",
      boxSizing: "border-box" as const,
    },

    dealerGrid: {
      height: "200px",
      width: "100%",
      overflowX: "auto" as const,
      overflowY: "auto" as const,
      border: "1px solid #82A0F6",
      backgroundColor: "#FFFFFF",
      boxSizing: "border-box" as const,
    },

    dealerTable: {
      width: "100%",
      borderCollapse: "collapse" as const,
      borderSpacing: 0,
    },

    button: {
      width: "100px",
      cursor: "pointer",
      margin: "0 3px",
    },

    disabledButton: {
      width: "100px",
      cursor: "default",
      margin: "0 3px",
      opacity: 0.65,
    },
  };

  const renderImage = (
    key: ImageKey,
    imageStyle: React.CSSProperties,
    alt: string,
  ) => {
    const source = getImageSource(key);

    if (!source) {
      return null;
    }

    return <img src={source} alt={alt} style={imageStyle} />;
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        {/* POPUP HEADER */}
        <div style={styles.popupHeader}>
          <span style={{ fontSize: "20px" }}>Add Edit Avatar</span>

          <img
            src={alert_close_n}
            alt="Close"
            onClick={onClose}
            style={{
              cursor: "pointer",
              width: "60px",
            }}
            onMouseOver={(event) => {
              event.currentTarget.src = alert_close_o;
            }}
            onMouseOut={(event) => {
              event.currentTarget.src = alert_close_n;
            }}
          />
        </div>

        {/* VB MAROON TITLE */}
        <div style={styles.maroonHeader}>
          {mode === "A" ? "Add New Avatar" : "View / Modify Avatar"}
        </div>

        {loading && <Loading />}

        <div style={styles.body}>
          <table
            border={0}
            cellPadding={0}
            cellSpacing={0}
            style={styles.mainTable}
          >
            <tbody>
              <tr>
                {/* LEFT 40% */}
                <td style={styles.leftColumn}>
                  <table
                    border={0}
                    cellPadding={5}
                    cellSpacing={0}
                    style={styles.leftInnerTable}
                  >
                    <tbody>
                      {/* TYPE */}
                      <tr>
                        <td style={styles.labelCell}>Type</td>

                        <td style={styles.valueCell}>
                          <label
                            style={{ marginRight: "15px", cursor: "pointer" }}
                          >
                            <input
                              type="radio"
                              name="avatarType"
                              checked={type === "Picture"}
                              onChange={() => handleTypeChange("Picture")}
                            />{" "}
                            Picture
                          </label>

                          <label
                            style={{ marginRight: "15px", cursor: "pointer" }}
                          >
                            <input
                              type="radio"
                              name="avatarType"
                              checked={type === "Animated"}
                              onChange={() => handleTypeChange("Animated")}
                            />{" "}
                            Animated
                          </label>

                          <label style={{ cursor: "pointer" }}>
                            <input
                              type="radio"
                              name="avatarType"
                              checked={type === "Interactive"}
                              onChange={() => handleTypeChange("Interactive")}
                            />{" "}
                            Interactive
                          </label>
                        </td>
                      </tr>

                      {/* TITLE + UPLOAD (upload visible in ADD and MODIFY) */}
                      <tr>
                        <td style={styles.labelCell}>Title</td>

                        <td
                          style={{
                            ...styles.valueCell,
                            whiteSpace: "nowrap",
                          }}
                        >
                          <span style={styles.titleBox}>{selectedTitle}</span>

                          <div
                            ref={dropAreaRef}
                            style={styles.uploadDrop}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            onClick={(event) => {
                              if (
                                (event.target as HTMLElement).id ===
                                "UploadImage"
                              ) {
                                return;
                              }

                              fileInputRef.current?.click();
                            }}
                          >
                            <div style={{ fontWeight: "bold" }}>
                              Drag & Drop 5 PNG images here
                            </div>

                            <div
                              style={{ fontSize: "11px", marginTop: "5px" }}
                            >
                              or click Upload Image
                            </div>

                            <input
                              ref={fileInputRef}
                              id="fuUploadImage"
                              type="file"
                              accept=".png,image/png"
                              multiple
                              style={{ display: "none" }}
                              onChange={handleFileInputChange}
                            />

                            <input
                              id="UploadImage"
                              type="button"
                              value="Upload Image"
                              style={{ marginTop: "7px", cursor: "pointer" }}
                              onClick={(event) => {
                                event.stopPropagation();
                                fileInputRef.current?.click();
                              }}
                            />
                          </div>
                        </td>
                      </tr>

                      {/* IMAGE AREA */}
                      <tr>
                        <td
                          colSpan={2}
                          style={{ textAlign: "center", padding: "5px" }}
                        >
                          <table
                            border={0}
                            cellPadding={5}
                            cellSpacing={0}
                            style={styles.imageOuter}
                          >
                            <tbody>
                              <tr>
                                {/* MAIN */}
                                <td rowSpan={2} style={styles.mainImageCell}>
                                  {renderImage(
                                    "avatar",
                                    styles.mainImage,
                                    selectedTitle,
                                  )}
                                </td>

                                {/* A */}
                                <td
                                  style={{
                                    ...styles.smallImageCell,
                                    width: "15%",
                                  }}
                                >
                                  {renderImage(
                                    "a",
                                    styles.resultImage,
                                    `${selectedTitle}_a`,
                                  )}
                                </td>

                                {/* B */}
                                <td
                                  style={{
                                    ...styles.smallImageCellLast,
                                    width: "15%",
                                  }}
                                >
                                  {renderImage(
                                    "b",
                                    styles.resultImage,
                                    `${selectedTitle}_b`,
                                  )}
                                </td>
                              </tr>

                              <tr>
                                {/* C */}
                                <td
                                  style={{
                                    ...styles.smallImageCell,
                                    borderBottom: "none",
                                  }}
                                >
                                  {renderImage(
                                    "c",
                                    styles.resultImage,
                                    `${selectedTitle}_c`,
                                  )}
                                </td>

                                {/* D */}
                                <td
                                  style={{
                                    ...styles.smallImageCellLast,
                                    borderBottom: "none",
                                  }}
                                >
                                  {renderImage(
                                    "d",
                                    styles.resultImage,
                                    `${selectedTitle}_d`,
                                  )}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </td>
                      </tr>

                      {/* START DATE */}
                      <tr>
                        <td style={styles.labelCell}>Start Date</td>

                        <td style={styles.valueCell}>
                          <input
                            type="date"
                            value={startDate}
                            onChange={(event) => setStartDate(event.target.value)}
                            style={{ ...styles.cssTextBox, width: "140px" }}
                          />
                          &nbsp;(GMT)
                        </td>
                      </tr>

                      {/* PRICE */}
                      <tr>
                        <td style={styles.labelCell}>Price</td>

                        <td style={styles.valueCell}>
                          <input
                            type="text"
                            inputMode="numeric"
                            maxLength={6}
                            value={price}
                            onChange={(event) => {
                              setPrice(
                                event.target.value.replace(/[^0-9]/g, ""),
                              );
                            }}
                            style={{ ...styles.cssTextBox, width: "33%" }}
                          />
                          &nbsp;(Max Char. 999999)
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </td>

                {/* VB 1% SPACER */}
                <td style={styles.rightSpacer}>&nbsp;</td>

                {/* RIGHT 59% */}
                <td style={styles.rightColumn}>
                  <table
                    border={0}
                    cellPadding={3}
                    cellSpacing={0}
                    style={styles.allocationTable}
                  >
                    <tbody>
                      {/* SCHOOL ALLOCATION */}
                      <tr>
                        <td colSpan={3} style={styles.allocationHeader}>
                          <label
                            style={{ cursor: "pointer", fontWeight: "bold" }}
                          >
                            <input
                              type="checkbox"
                              checked={chkSchools}
                              onChange={handleSchoolsToggle}
                            />{" "}
                            Allocated to schools in
                          </label>
                        </td>
                      </tr>

                      <tr>
                        <td
                          colSpan={3}
                          style={{ padding: "2px", height: "200px" }}
                        >
                          <div
                            style={{
                              ...styles.allocationGrid,
                              opacity: chkSchools ? 1 : 0.65,
                            }}
                          >
                            <table style={styles.countryTable}>
                              <thead>
                                <tr style={styles.countryHeader}>
                                  <td
                                    style={{
                                      ...styles.countryHeaderCell,
                                      width: "10%",
                                    }}
                                  >
                                    &nbsp;
                                  </td>

                                  <td
                                    style={{
                                      ...styles.countryHeaderCell,
                                      width: "90%",
                                    }}
                                  >
                                    Country
                                  </td>
                                </tr>
                              </thead>

                              <tbody>
                                {countries.map((country, index) => {
                                  const selected =
                                    selectedSchoolCountries.includes(country);

                                  return (
                                    <tr
                                      key={`school-${country}`}
                                      style={{
                                        backgroundColor: selected
                                          ? "#FFFFCC"
                                          : index % 2 === 0
                                            ? "#F4FAFF"
                                            : "#FFFFFF",
                                      }}
                                    >
                                      <td
                                        style={{
                                          ...styles.countryCell,
                                          textAlign: "center",
                                        }}
                                      >
                                        <input
                                          type="checkbox"
                                          disabled={!chkSchools}
                                          checked={selected}
                                          onChange={() =>
                                            toggleSchoolCountry(country)
                                          }
                                        />
                                      </td>

                                      <td style={styles.countryCell}>
                                        {country}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>

                      {/* SPACE BETWEEN SCHOOL / HOME */}
                      <tr>
                        <td colSpan={3} style={{ height: "15px" }}>
                          &nbsp;
                        </td>
                      </tr>

                      {/* HOME ALLOCATION */}
                      <tr>
                        <td
                          colSpan={3}
                          style={{ ...styles.allocationHeader, width: "30%" }}
                        >
                          <label
                            style={{ cursor: "pointer", fontWeight: "bold" }}
                          >
                            <input
                              type="checkbox"
                              checked={chkHomes}
                              onChange={handleHomesToggle}
                            />{" "}
                            Allocated to home users
                          </label>
                        </td>
                      </tr>

                      {/* HOME COUNTRY + DEALER */}
                      <tr>
                        <td colSpan={3} style={{ padding: "2px" }}>
                          <div
                            style={{
                              ...styles.homeCountryWrapper,
                              opacity: chkHomes ? 1 : 0.65,
                            }}
                          >
                            {/* HOME COUNTRY */}
                            <div style={styles.homeCountryColumn}>
                              <div style={styles.homeGrid}>
                                <table style={styles.countryTable}>
                                  <thead>
                                    <tr style={styles.countryHeader}>
                                      <td
                                        style={{
                                          ...styles.countryHeaderCell,
                                          width: "10%",
                                        }}
                                      >
                                        &nbsp;
                                      </td>

                                      <td
                                        style={{
                                          ...styles.countryHeaderCell,
                                          width: "90%",
                                        }}
                                      >
                                        Country
                                      </td>
                                    </tr>
                                  </thead>

                                  <tbody>
                                    {countries.map((country, index) => {
                                      const selected =
                                        selectedHomeCountries.includes(country);

                                      return (
                                        <tr
                                          key={`home-${country}`}
                                          style={{
                                            backgroundColor: selected
                                              ? "#FFFFCC"
                                              : index % 2 === 0
                                                ? "#F4FAFF"
                                                : "#FFFFFF",
                                          }}
                                        >
                                          <td
                                            style={{
                                              ...styles.countryCell,
                                              textAlign: "center",
                                            }}
                                          >
                                            <input
                                              type="checkbox"
                                              disabled={!chkHomes}
                                              checked={selected}
                                              onChange={() =>
                                                toggleHomeCountry(country)
                                              }
                                            />
                                          </td>

                                          <td style={styles.countryCell}>
                                            {country}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>

                            {/* DEALER */}
                            <div style={styles.dealerColumn}>
                              <div style={styles.dealerGrid}>
                                <table style={styles.dealerTable}>
                                  <thead>
                                    <tr style={styles.countryHeader}>
                                      <td
                                        style={{
                                          ...styles.countryHeaderCell,
                                          width: "7%",
                                        }}
                                      >
                                        &nbsp;
                                      </td>

                                      <td
                                        style={{
                                          ...styles.countryHeaderCell,
                                          width: "70%",
                                        }}
                                      >
                                        eDealer
                                      </td>

                                      <td
                                        style={{
                                          ...styles.countryHeaderCell,
                                          width: "23%",
                                        }}
                                      >
                                        Country
                                      </td>
                                    </tr>
                                  </thead>

                                  <tbody>
                                    {dealers.map((dealer, index) => {
                                      const selected = selectedDealers.some(
                                        (item) =>
                                          item.Country === dealer.Country &&
                                          item.SchoolCode === dealer.SchoolCode,
                                      );

                                      return (
                                        <tr
                                          key={`${dealer.Country}-${dealer.SchoolCode}`}
                                          style={{
                                            backgroundColor:
                                              index % 2 === 0
                                                ? "#F4FAFF"
                                                : "#FFFFFF",
                                          }}
                                        >
                                          <td
                                            style={{
                                              ...styles.countryCell,
                                              textAlign: "center",
                                            }}
                                          >
                                            <input
                                              type="checkbox"
                                              disabled={!chkHomes}
                                              checked={selected}
                                              onChange={() =>
                                                toggleDealer(dealer)
                                              }
                                            />
                                          </td>

                                          {/* VB: ali gn="right" is a typo,
                                              so the cell is left-aligned. */}
                                          <td
                                            style={{
                                              ...styles.countryCell,
                                              textAlign: "left",
                                            }}
                                          >
                                            {dealer.Name}
                                          </td>

                                          <td style={styles.countryCell}>
                                            {dealer.Country}
                                          </td>
                                        </tr>
                                      );
                                    })}

                                    {dealers.length === 0 && (
                                      <tr>
                                        <td
                                          colSpan={3}
                                          style={{
                                            padding: "10px",
                                            textAlign: "center",
                                            color: "#777",
                                          }}
                                        >
                                          {chkHomes &&
                                          selectedHomeCountries.length > 0
                                            ? "No eDealers found."
                                            : ""}
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </td>
              </tr>

              {/* SPACE */}
              <tr>
                <td
                  colSpan={3}
                  style={{ textAlign: "center", height: "15px" }}
                >
                  &nbsp;
                </td>
              </tr>

              {/* BUTTONS */}
              <tr>
                <td
                  colSpan={3}
                  style={{ textAlign: "center", paddingBottom: "10px" }}
                >
                  <button
                    type="button"
                    disabled={!saveEnabled || saving}
                    onClick={handleSave}
                    style={
                      saveEnabled && !saving
                        ? styles.button
                        : styles.disabledButton
                    }
                  >
                    {saving ? "Saving..." : "Save"}
                  </button>
                  &nbsp;
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={saving}
                    style={styles.button}
                  >
                    Cancel
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default EL_AvatarARD;